import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as LayoutLeftIcon } from '../../../icons/layout-left.svg';
import { ReactComponent as HomeIcon } from '../../../icons/nav-links/home.svg';
import { ReactComponent as SettingsIcon } from '../../../icons/nav-links/settings.svg';
import { ReactComponent as WhatsappIcon } from '../../../icons/whatsapp-color.svg';
import { useAgentWhatsappNumber } from '../../../hooks/useAgentWhatsappNumber';
import { settingsLinkState } from '../../../utils/settingsReturn';
import './mobile-app-shell.scss';

export interface MobileAppMenuEntry {
  id: string;
  name: string;
  /** Line icon for built-in apps. Entries without one are listed under "Your apps". */
  icon?: React.ReactNode;
  active?: boolean;
  onSelect: () => void;
}

interface MobileAppMenuProps {
  open: boolean;
  onClose: () => void;
  entries: MobileAppMenuEntry[];
  onHome: () => void;
}

/**
 * Full-screen apps menu opened from MobileAppHeader (Figma 5227:17436). Lists
 * the built-in apps (Reminders, Owed replies, Documents, Inbox Manager, plus
 * Key Dates / Trips when the user has them) and then the user's own apps.
 */
const MobileAppMenu: React.FC<MobileAppMenuProps> = ({
  open,
  onClose,
  entries,
  onHome,
}) => {
  const { whatsappLink } = useAgentWhatsappNumber();
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const builtIn = entries.filter((e) => e.icon);
  const userApps = entries.filter((e) => !e.icon);

  const renderEntry = (entry: MobileAppMenuEntry) => (
    <button
      key={entry.id}
      type='button'
      className={`mobile-app-menu-item${entry.active ? ' active' : ''}`}
      aria-current={entry.active ? 'page' : undefined}
      onClick={() => {
        entry.onSelect();
        onClose();
      }}
    >
      {entry.icon && (
        <span className='mobile-app-menu-item-icon'>{entry.icon}</span>
      )}
      <span className='mobile-app-menu-item-name'>{entry.name}</span>
    </button>
  );

  return createPortal(
    <div
      className='mobile-app-menu'
      role='dialog'
      aria-modal='true'
      aria-label='Apps'
      data-testid='mobile-app-menu'
    >
      <div className='mobile-app-header mobile-app-menu-header'>
        <button
          type='button'
          className='mobile-app-header-btn'
          onClick={onClose}
          aria-label='Close apps menu'
        >
          <LayoutLeftIcon />
        </button>
        <div className='mobile-app-header-actions'>
          <Link
            to='/settings'
            state={settingsLinkState(location)}
            className='mobile-app-menu-link'
            onClick={onClose}
          >
            <SettingsIcon />
            <span>Settings</span>
          </Link>
          {whatsappLink && (
            <a
              href={whatsappLink}
              target='_blank'
              rel='noreferrer'
              className='mobile-app-menu-link'
            >
              <WhatsappIcon />
              <span>WhatsApp</span>
            </a>
          )}
        </div>
      </div>

      <nav className='mobile-app-menu-body'>
        <div className='mobile-app-menu-group'>
          <button
            type='button'
            className='mobile-app-menu-item'
            onClick={() => {
              onHome();
              onClose();
            }}
          >
            <span className='mobile-app-menu-item-icon'>
              <HomeIcon />
            </span>
            <span className='mobile-app-menu-item-name'>Home</span>
          </button>
          {builtIn.map(renderEntry)}
        </div>

        {userApps.length > 0 && (
          <div className='mobile-app-menu-group'>
            <p className='mobile-app-menu-label'>Your apps</p>
            {userApps.map(renderEntry)}
          </div>
        )}
      </nav>
    </div>,
    document.body,
  );
};

export default MobileAppMenu;
